import { KycRevealProvider } from '../../../components/KYC/reveal';
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    Paper,
    Typography,
    makeStyles,
    CircularProgress,
} from '@material-ui/core';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import moment from 'moment';

// Components
import { Spinner, LightBoxImage } from '../../../components/UI';
import BackgroundVerification from '../../../components/Containers/BGVStatus';
import BGVStatusV2 from '../../../components/Containers/BGVStatusV2';
import RetriggerBGVModal from '../../../components/Modals/RetriggerBGVModal';
import EditableKYCDocumentCard, {
    KYCDocumentPayload,
    KYCDocumentNotProvidedCard,
    DocumentType,
} from '../../../components/KYC/EditableKYCDocumentCard';
import { KYCApprovalModal } from '../../../components/KYC';
import { AadhaarFailureIcon, AadhaarSuccessIcon } from '../../../assets/svg';

// Actions
import {
    getKycDetails,
    postKycDetails,
    updateFatherName,
    getSingleStaff,
    initiateDlVerification,
    initiatePanVerification,
    confirmAddressVerification,
    triggerBGVManual,
} from '../../../store/actions/staffActionCreator';
import { enqueueSnackbar } from '../../../store/actions/notificationActionCreator';

// Utils
import {
    validateDL,
    isDigilockerVerified,
    isBlueDartOrg,
    isBlueDartVendorRole,
} from '../../../utils/appHelper';

// ============================================================================
// Types & Interfaces
// ============================================================================

interface SourceDetails {
    source?: string;
    remarks?: string;
}

interface StaffInfo {
    id: number;
    sourceDetails: SourceDetails;
    name?: string;
    contactNumber?: string;
    status?: string;
}

interface BGVStatusItem {
    state: string;
    initiatedAt: string;
    status?: string;
    caseSeverity?: string;
}

interface UidaiAadhar {
    kycPhotoS3Url?: string;
    name?: string;
    dateOfBirth?: string;
    address?: string;
}

interface HomeSelfie {
    id?: number;
    image?: string | null;
    approvalStatus?: string | null;
    faceMatchConfidence?: number | null;
    faceMatchScore?: number | null;
    latitude?: number | null;
    longitude?: number | null;
    address?: string | null;
    rejectReason?: string | null;
    verified?: boolean;
    addressVerified?: boolean;
}

interface KycInfo {
    aadhar?: KYCDocumentPayload;
    panCard?: KYCDocumentPayload;
    rc?: KYCDocumentPayload;
    drivingLicense?: KYCDocumentPayload;
    autoVerified?: boolean;
    aadharKycRequired?: boolean;
    uidaiKycRequired?: boolean;
    uidaiKycVerified?: boolean;
    uidaiAadhar?: UidaiAadhar;
    s3ProfileLink?: string;
    selfie?: { image?: string };
    homeSelfie?: HomeSelfie;
    bgvStatus?: BGVStatusItem[];
    userBgvStatus?: string | null;
    dlRequiredForBGV?: boolean;
    emiratesId?: KYCDocumentPayload;
    emiratesDl?: KYCDocumentPayload;
    insuranceCard?: KYCDocumentPayload;
    visaCopy?: KYCDocumentPayload;
    passport?: KYCDocumentPayload;
    // BlueDart document types
    pccAppliedCopy?: KYCDocumentPayload;
    pccFinalClearance?: KYCDocumentPayload;
    kycDocPagesConfig?: Record<
        string,
        {
            files?: number;
            isDateInputNeeded?: boolean;
            isTextInputNeeded?: boolean;
        }
    >;
    requiredKycDocs?: string[];
    optionalKycDocs?: string[];
    iqamaId?: KYCDocumentPayload;
}

interface ApprovalModalState {
    open: boolean;
    documentId: number;
    approvalStatus: 'APPROVED' | 'REJECTED';
}

interface RootState {
    staff?: {
        kycInfo?: KycInfo;
    };
    settings?: {
        basicDetails?: {
            vendorName?: string;
        };
    };
}

interface VerifyKycV2Props {
    staffInfo: StaffInfo;
}

// ============================================================================
// Styles
// ============================================================================

const useStyles = makeStyles((theme) => ({
    container: {
        marginBottom: 16,
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
    },
    sectionTitle: {
        fontSize: '20px',
        fontWeight: 700,
        color: '#14112d',
    },
    dividerLine: {
        width: '98%',
        marginLeft: 10,
    },
    sourceCard: {
        display: 'flex',
        justifyContent: 'flex-start',
        flexDirection: 'column',
        border: '1px solid rgba(38, 50, 56, 0.16)',
        borderRadius: '8px',
        padding: '16px',
    },
    sourceLabel: {
        fontFamily: 'Nunito',
        fontSize: '14px',
        fontWeight: 400,
        color: '#14112d',
    },
    sourceValue: {
        fontFamily: 'Nunito',
        fontSize: '14px',
        fontWeight: 700,
        color: '#14112d',
    },
    selfieContainer: {
        marginBottom: 16,
    },
    selfieTitle: {
        fontSize: '20px',
        fontWeight: 700,
        color: '#14112d',
        marginBottom: 12,
    },
    uidaiStatusContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginLeft: '1rem',
    },
    uidaiStatusText: {
        fontSize: '18px',
        fontWeight: 700,
        marginLeft: 5,
    },
    homeSelfieCard: {
        border: '1px solid rgba(38, 50, 56, 0.12)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        background: '#ffffff',
    },
    homeSelfieImageWrapper: {
        width: 120,
        height: 120,
        borderRadius: '12px',
        overflow: 'hidden',
        border: '2px solid rgba(38, 50, 56, 0.16)',
        flexShrink: 0,
    },
    homeSelfieImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover' as const,
    },
    confidenceBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '20px',
        padding: '4px 12px',
        fontWeight: 700,
        fontSize: '14px',
        gap: '4px',
    },
    viewMapBtn: {
        textTransform: 'none' as const,
        fontWeight: 700,
        fontSize: '13px',
        borderRadius: '8px',
        padding: '4px 14px',
    },
    metaLabel: {
        fontSize: '12px',
        color: '#78909c',
        fontWeight: 600,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        marginBottom: '2px',
    },
    metaValue: {
        fontSize: '14px',
        color: '#14112d',
        fontWeight: 700,
    },
}));

// ============================================================================
// Sub-Components
// ============================================================================

interface SourceDetailsSectionProps {
    sourceDetails: SourceDetails;
    classes: ReturnType<typeof useStyles>;
}

const SourceDetailsSection: React.FC<SourceDetailsSectionProps> = ({
    sourceDetails,
    classes,
}) => (
    <Paper
        style={{ marginBottom: '16px' }}
        elevation={0}
        className={classes.container}
    >
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <Box className={classes.sectionHeader}>
                    <Typography
                        className={classes.sectionTitle}
                        variant="h6"
                        component="span"
                        gutterBottom
                    >
                        DE Source Details
                    </Typography>
                    <div style={{ flexGrow: 1 }}>
                        <Divider className={classes.dividerLine} />
                    </div>
                </Box>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Box className={classes.sourceCard}>
                        <Typography
                            className={classes.sourceLabel}
                            variant="body2"
                            component="span"
                            gutterBottom
                        >
                            Source
                        </Typography>
                        <Typography
                            className={classes.sourceValue}
                            variant="body2"
                            component="span"
                            gutterBottom
                        >
                            {sourceDetails?.source || '-'}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Box className={classes.sourceCard}>
                        <Typography
                            className={classes.sourceLabel}
                            variant="body2"
                            component="span"
                            gutterBottom
                        >
                            Remarks
                        </Typography>
                        <Typography
                            className={classes.sourceValue}
                            variant="body2"
                            component="span"
                            gutterBottom
                        >
                            {sourceDetails?.remarks || '-'}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Grid>
    </Paper>
);

interface SelfieSectionProps {
    profileImage: string | null;
    classes: ReturnType<typeof useStyles>;
}

const SelfieSection: React.FC<SelfieSectionProps> = ({
    profileImage,
    classes,
}) => (
    <Box className={classes.selfieContainer}>
        <Typography
            className={classes.selfieTitle}
            variant="h6"
            component="span"
            gutterBottom
        >
            Selfie
        </Typography>
        {profileImage ? (
            <LightBoxImage
                containerStyles={{ width: 120, height: 120, marginRight: 5 }}
                imageStyles={{
                    width: 120,
                    height: 120,
                    margin: 'auto',
                    borderRadius: '6px',
                    objectFit: 'fill',
                    border: '1px solid rgba(38, 50, 56, 0.32)',
                }}
                source={profileImage}
            />
        ) : (
            <Typography style={{ color: '#9e9e9e' }}>-</Typography>
        )}
    </Box>
);

interface UidaiStatusBadgeProps {
    uidaiKycVerified: boolean;
    classes: ReturnType<typeof useStyles>;
}

const UidaiStatusBadge: React.FC<UidaiStatusBadgeProps> = ({
    uidaiKycVerified,
    classes,
}) => (
    <Box className={classes.uidaiStatusContainer}>
        <div style={{ marginLeft: '1rem' }}>
            {uidaiKycVerified ? (
                <AadhaarSuccessIcon style={{ width: '20px' }} />
            ) : (
                <AadhaarFailureIcon style={{ width: '20px' }} />
            )}
        </div>
        <Typography
            className={classes.uidaiStatusText}
            style={{ color: uidaiKycVerified ? '#4caf50' : '#f44336' }}
            variant="h6"
            component="span"
            gutterBottom
        >
            {uidaiKycVerified ? 'UIDAI Verified' : 'UIDAI Verification Pending'}
        </Typography>
    </Box>
);

// ============================================================================
// HomeSelfieCard Sub-Component
// ============================================================================

interface HomeSelfieCardProps {
    homeSelfie: HomeSelfie;
    classes: ReturnType<typeof useStyles>;
    staffId: number;
    onAddressVerified: () => void;
}

const getConfidenceColor = (
    confidence: number | null | undefined,
): { bg: string; text: string; label: string } => {
    if (confidence == null)
        return { bg: '#f5f5f5', text: '#9e9e9e', label: 'N/A' };
    if (confidence >= 85)
        return { bg: '#e8f5e9', text: '#2e7d32', label: 'High' };
    if (confidence >= 65)
        return { bg: '#fff8e1', text: '#f57f17', label: 'Medium' };
    return { bg: '#ffebee', text: '#c62828', label: 'Low' };
};

const getApprovalStatusChip = (status: string | null | undefined) => {
    switch (status) {
        case 'APPROVED':
            return {
                icon: <CheckCircleIcon style={{ fontSize: 16 }} />,
                label: 'Approved',
                bg: '#e8f5e9',
                color: '#2e7d32',
            };
        case 'REJECTED':
            return {
                icon: <CancelIcon style={{ fontSize: 16 }} />,
                label: 'Rejected',
                bg: '#ffebee',
                color: '#c62828',
            };
        default:
            return {
                icon: <HourglassEmptyIcon style={{ fontSize: 16 }} />,
                label: status || 'Pending',
                bg: '#fff8e1',
                color: '#f57f17',
            };
    }
};

const HomeSelfieCard: React.FC<HomeSelfieCardProps> = ({
    homeSelfie,
    classes,
    staffId,
    onAddressVerified,
}) => {
    const dispatch = useDispatch();
    const faceMatchValue = isBlueDartOrg()
        ? homeSelfie.faceMatchScore != null && homeSelfie.faceMatchScore > 0
            ? homeSelfie.faceMatchScore
            : null
        : homeSelfie.faceMatchConfidence;
    const confidenceStyle = getConfidenceColor(faceMatchValue);
    const statusChip = getApprovalStatusChip(homeSelfie.approvalStatus);
    const hasLocation =
        homeSelfie.latitude != null && homeSelfie.longitude != null;

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const isAddressVerified = !!homeSelfie.addressVerified;

    const handleViewInMap = () => {
        window.open(
            `https://www.google.com/maps?q=${homeSelfie.latitude},${homeSelfie.longitude}`,
            '_blank',
            'noopener,noreferrer',
        );
    };

    const handleConfirmAddress = () => {
        setConfirmLoading(true);
        dispatch(
            confirmAddressVerification(
                staffId,
                () => {
                    setConfirmLoading(false);
                    setConfirmDialogOpen(false);
                    onAddressVerified();
                },
                () => {
                    setConfirmLoading(false);
                    setConfirmDialogOpen(false);
                },
            ) as any,
        );
    };

    return (
        <Paper elevation={0} className={classes.homeSelfieCard}>
            {/* Section Header */}
            <Box className={classes.sectionHeader} style={{ marginBottom: 16 }}>
                <Typography
                    className={classes.sectionTitle}
                    variant="h6"
                    component="span"
                >
                    Home Selfie
                </Typography>
                <div style={{ flexGrow: 1 }}>
                    <Divider className={classes.dividerLine} />
                </div>
            </Box>

            {/* Card Body */}
            <Grid container spacing={3} alignItems="flex-start">
                {/* Profile Image */}
                <Grid item>
                    <div className={classes.homeSelfieImageWrapper}>
                        {homeSelfie.image ? (
                            <LightBoxImage
                                containerStyles={{ width: 120, height: 120 }}
                                imageStyles={{
                                    width: 120,
                                    height: 120,
                                    objectFit: 'cover',
                                    borderRadius: '10px',
                                }}
                                source={homeSelfie.image}
                            />
                        ) : (
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    background: '#f5f5f5',
                                }}
                            >
                                <Typography
                                    style={{ color: '#bdbdbd', fontSize: 12 }}
                                >
                                    No Image
                                </Typography>
                            </Box>
                        )}
                    </div>
                </Grid>

                {/* Details */}
                <Grid item xs>
                    <Grid container spacing={2}>
                        {/* Approval Status */}
                        {/* <Grid item xs={12} sm={4}>
                            <Typography className={classes.metaLabel}>
                                Approval Status
                            </Typography>
                            <Box
                                display="inline-flex"
                                alignItems="center"
                                style={{
                                    gap: 6,
                                    padding: '5px 12px',
                                    borderRadius: 20,
                                    background: statusChip.bg,
                                    color: statusChip.color,
                                    fontWeight: 700,
                                    fontSize: 13,
                                    marginTop: 2,
                                }}
                            >
                                {statusChip.icon}
                                <span>{statusChip.label}</span>
                            </Box>
                        </Grid> */}

                        {/* Face Match Score */}
                        <Grid item xs={12} sm={4}>
                            <Typography className={classes.metaLabel}>
                                Face Match Score
                            </Typography>
                            <Box
                                display="inline-flex"
                                alignItems="center"
                                style={{
                                    gap: 6,
                                    padding: '5px 14px',
                                    borderRadius: 20,
                                    background: confidenceStyle.bg,
                                    color: confidenceStyle.text,
                                    fontWeight: 700,
                                    fontSize: 13,
                                    marginTop: 2,
                                }}
                            >
                                <span>
                                    {faceMatchValue != null
                                        ? `${faceMatchValue}%`
                                        : 'N/A'}
                                </span>
                                <span style={{ fontSize: 11, opacity: 0.8 }}>
                                    ({confidenceStyle.label})
                                </span>
                            </Box>
                        </Grid>

                        {/* Location + Address Confirmation */}
                        <Grid item xs={12} sm={8}>
                            <Typography className={classes.metaLabel}>
                                Location
                            </Typography>
                            <Box
                                display="flex"
                                alignItems="flex-start"
                                style={{ marginTop: 8 }}
                            >
                                {/* LEFT SIDE */}
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="flex-start"
                                >
                                    {hasLocation ? (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            className={classes.viewMapBtn}
                                            style={{
                                                borderColor: '#1976d2',
                                                color: '#1976d2',
                                                marginRight: 12,
                                            }}
                                            startIcon={
                                                <LocationOnIcon
                                                    style={{ fontSize: 16 }}
                                                />
                                            }
                                            onClick={handleViewInMap}
                                        >
                                            View in Map
                                        </Button>
                                    ) : (
                                        <Typography
                                            className={classes.metaValue}
                                        >
                                            —
                                        </Typography>
                                    )}
                                    {!!homeSelfie.address && (
                                        <Typography
                                            style={{
                                                fontSize: 12,
                                                color: '#555',
                                                marginTop: 6,
                                                maxWidth: 260,
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {homeSelfie.address}
                                        </Typography>
                                    )}
                                </Box>

                                {/* RIGHT SIDE */}
                                <Box style={{ marginLeft: 8 }}>
                                    {isAddressVerified ? (
                                        <Chip
                                            icon={
                                                <CheckCircleIcon
                                                    style={{
                                                        fontSize: 16,
                                                        color: '#2e7d32',
                                                    }}
                                                />
                                            }
                                            label="Address Verified"
                                            size="small"
                                            style={{
                                                background: '#e8f5e9',
                                                color: '#2e7d32',
                                                fontWeight: 700,
                                                fontSize: 13,
                                                borderRadius: 20,
                                            }}
                                        />
                                    ) : (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            style={{
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                fontSize: 13,
                                                borderRadius: 8,
                                                padding: '4px 14px',
                                                background: '#ff9800',
                                                color: '#fff',
                                            }}
                                            startIcon={
                                                <VerifiedUserIcon
                                                    style={{ fontSize: 16 }}
                                                />
                                            }
                                            onClick={() =>
                                                setConfirmDialogOpen(true)
                                            }
                                        >
                                            Confirm Address
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Grid>

                        {/* Reject Reason (conditional) */}
                        {homeSelfie.rejectReason && (
                            <Grid item xs={12}>
                                <Typography className={classes.metaLabel}>
                                    Reject Reason
                                </Typography>
                                <Typography
                                    className={classes.metaValue}
                                    style={{ color: '#c62828', marginTop: 2 }}
                                >
                                    {homeSelfie.rejectReason}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>

            {/* Address Confirmation Dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={() => !confirmLoading && setConfirmDialogOpen(false)}
                aria-labelledby="confirm-address-dialog-title"
                maxWidth="sm"
            >
                <DialogTitle id="confirm-address-dialog-title">
                    Confirm Address Verification
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Please confirm if you have visited the rider's address
                        and verified personally.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDialogOpen(false)}
                        color="default"
                        disabled={confirmLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmAddress}
                        color="primary"
                        variant="contained"
                        disabled={confirmLoading}
                        startIcon={
                            confirmLoading ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : null
                        }
                    >
                        {confirmLoading ? 'Confirming...' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

// ============================================================================
// Custom Hooks
// ============================================================================

interface UseDocumentStateReturn {
    payload: KYCDocumentPayload | undefined;
    setPayload: React.Dispatch<
        React.SetStateAction<KYCDocumentPayload | undefined>
    >;
    isEdited: boolean;
    setIsEdited: React.Dispatch<React.SetStateAction<boolean>>;
    updateField: (field: string, value: any) => void;
}

const useDocumentState = (
    initialData: KYCDocumentPayload | undefined,
): UseDocumentStateReturn => {
    const [payload, setPayload] = useState<KYCDocumentPayload | undefined>(
        initialData,
    );
    const [isEdited, setIsEdited] = useState(false);

    useEffect(() => {
        setPayload(initialData);
        setIsEdited(false);
    }, [initialData]);

    const updateField = useCallback((field: string, value: any) => {
        setPayload((prev) =>
            prev ? { ...prev, [field]: value } : { [field]: value },
        );
        setIsEdited(true);
    }, []);
    let modifiedPayload = {
        ...payload,
        docDateMilliSeconds: payload?.docDateMilliSeconds
            ? payload.docDateMilliSeconds
            : payload?.docDate
              ? moment(payload.docDate).valueOf()
              : payload?.autoKycDocumentDob
                ? moment(payload.autoKycDocumentDob).valueOf()
                : null,
    };
    return {
        payload: modifiedPayload,
        setPayload,
        isEdited,
        setIsEdited,
        updateField,
    };
};

// ============================================================================
// Main Component
// ============================================================================

const VerifyKycV2: React.FC<VerifyKycV2Props> = ({ staffInfo }) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    // Redux state
    const kycInfo = useSelector((state: RootState) => state.staff?.kycInfo);
    const vendorName = useSelector(
        (state: RootState) => state.settings?.basicDetails?.vendorName,
    );
    const isAmazonOrg = vendorName === 'AMAZON';

    // Local state
    const [loading, setLoading] = useState(true);
    const [approvalModal, setApprovalModal] = useState<ApprovalModalState>({
        open: false,
        documentId: 0,
        approvalStatus: 'APPROVED',
    });
    const [retriggerBGVOpen, setRetriggerBGVOpen] = useState(false);
    // Only show docs that are required for this org
    const requiredDocs = new Set(kycInfo?.requiredKycDocs ?? []);
    const hasDoc = (type: string) => requiredDocs.has(type);

    // Document states using custom hook
    const aadharState = useDocumentState(kycInfo?.aadhar);
    const panState = useDocumentState(kycInfo?.panCard);
    const rcState = useDocumentState(kycInfo?.rc);

    // Middle East document states
    const emiratesIdState = useDocumentState(kycInfo?.emiratesId);
    const emiratesDlState = useDocumentState(kycInfo?.emiratesDl);
    const insuranceCardState = useDocumentState(kycInfo?.insuranceCard);
    const visaCopyState = useDocumentState(kycInfo?.visaCopy);
    const passportState = useDocumentState(kycInfo?.passport);
    const iqamaState = useDocumentState(kycInfo?.iqamaId);

    // BlueDart document states for PCC
    const pccAppliedCopyState = useDocumentState(kycInfo?.pccAppliedCopy);
    const pccFinalClearanceState = useDocumentState(kycInfo?.pccFinalClearance);
    const showPccDocs = isBlueDartOrg() && isBlueDartVendorRole();

    // DL state needs special handling for docDateMilliSeconds
    const [dlPayload, setDlPayload] = useState<
        KYCDocumentPayload | undefined
    >();
    const [isDlEdited, setIsDlEdited] = useState(false);

    useEffect(() => {
        if (kycInfo?.drivingLicense) {
            setDlPayload({
                ...kycInfo.drivingLicense,
                docDateMilliSeconds: kycInfo.drivingLicense.docDate
                    ? moment(kycInfo.drivingLicense.docDate).valueOf()
                    : kycInfo.drivingLicense.autoKycDocumentDob
                      ? moment(
                            kycInfo.drivingLicense.autoKycDocumentDob,
                        ).valueOf()
                      : null,
            });
        }
        setIsDlEdited(false);
    }, [kycInfo?.drivingLicense]);

    // Dispatch helpers
    const showMessage = useCallback(
        (msg: string, type: 'success' | 'error' = 'error') => {
            dispatch(
                enqueueSnackbar({
                    message: msg,
                    options: {
                        key: new Date().getTime() + Math.random(),
                        variant: type,
                    },
                }),
            );
        },
        [dispatch],
    );

    const dispatchGetKycDetails = useCallback(
        (id: number, onSuccess?: Function, onError?: Function) => {
            dispatch(getKycDetails(id, onSuccess, onError) as any);
        },
        [dispatch],
    );

    const dispatchPostKycDetails = useCallback(
        (payload: any, onSuccess?: Function, onError?: Function) => {
            dispatch(postKycDetails(payload, onSuccess, onError) as any);
        },
        [dispatch],
    );

    const dispatchGetSingleStaff = useCallback(
        (id: number, onSuccess?: Function, onError?: Function) => {
            dispatch(getSingleStaff(id, onSuccess, onError) as any);
        },
        [dispatch],
    );

    // Fetch KYC info on mount
    useEffect(() => {
        fetchKycInfo();
    }, []);

    const fetchKycInfo = async () => {
        setLoading(true);
        dispatchGetKycDetails(
            staffInfo?.id,
            () => setLoading(false),
            () => setLoading(false),
        );
    };

    // Computed values
    const isAadhaarVerifiedByDigiLocker = isDigilockerVerified(
        aadharState.payload,
    );
    const isPanVerifiedByDigiLocker = isDigilockerVerified(panState.payload);
    const isDLVerifiedByDigiLocker = isDigilockerVerified(dlPayload);
    console.log('kycInfo aadharState', isAadhaarVerifiedByDigiLocker);

    const isAadhaarAutoVerified = !!isBlueDartOrg()
        ? (!!kycInfo?.autoVerified ||
              aadharState?.payload?.verificationType === 'Auto Verification') &&
          aadharState.payload?.approvalStatus === 'APPROVED'
            ? true
            : false
        : !!kycInfo?.autoVerified &&
          !!aadharState.payload?.documentNo &&
          !!aadharState.payload?.imageFront &&
          !!aadharState.payload?.imageBack;
    const isPANAutoVerified = !!isBlueDartOrg()
        ? (!!kycInfo?.autoVerified ||
              panState?.payload?.verificationType === 'Auto Verification' ||
              panState?.payload?.dlCaseCheck === 'VERIFIED') &&
          panState.payload?.approvalStatus === 'APPROVED'
            ? true
            : false
        : !!kycInfo?.autoVerified &&
          !!panState.payload?.documentNo &&
          !!panState.payload?.image;

    const isRCAutoVerified = !!isBlueDartOrg()
        ? (!!kycInfo?.autoVerified ||
              rcState?.payload?.verificationType === 'Auto Verification') &&
          rcState.payload?.approvalStatus === 'APPROVED'
            ? true
            : false
        : !!kycInfo?.autoVerified &&
          !!rcState.payload?.documentNo &&
          !!rcState.payload?.image;

    const isDLAutoVerified = dlPayload?.dlCaseCheck === 'VERIFIED';
    const isDLFailed = dlPayload?.dlCaseCheck === 'INITIATION_FAILED';
    const enableDLUpdate =
        !dlPayload?.dlCaseCheck ||
        dlPayload?.dlCaseCheck === null ||
        isDLFailed;

    // Visibility conditions
    const hideAadhaar = !!isAadhaarVerifiedByDigiLocker
        ? false
        : !kycInfo?.autoVerified
          ? !(
                !!aadharState.payload?.documentNo &&
                !!aadharState.payload?.imageFront &&
                !!aadharState.payload?.imageBack
            )
          : false;

    const hidePAN =
        !!isPanVerifiedByDigiLocker || !!kycInfo?.autoVerified
            ? !(
                  panState.payload?.documentNo &&
                  (panState.payload?.image || panState.payload?.imageFront)
              )
            : false;

    const hideRC = !!kycInfo?.autoVerified
        ? !(
              rcState.payload?.documentNo &&
              (rcState.payload?.image ||
                  rcState.payload?.imageFront ||
                  rcState.payload?.imageBack)
          )
        : false;

    const hideDL = !dlPayload?.id;

    // Pending at ops conditions
    const isAadhaarPendingAtOps =
        aadharState.payload?.approvalStatus === 'PENDING_BD_OPS_APPROVAL';
    const isPanPendingAtOps =
        panState.payload?.approvalStatus === 'PENDING_BD_OPS_APPROVAL';
    const isRcPendingAtOps =
        rcState.payload?.approvalStatus === 'PENDING_BD_OPS_APPROVAL';
    const isDLPendingAtOps =
        dlPayload?.approvalStatus === 'PENDING_BD_OPS_APPROVAL';

    // BD DOB: shown for BlueDart org Aadhaar/PAN; editable only by the vendor dashboard role, and only
    // while the doc is still theirs to act on — rejected, or pending their approval. A PAN missing its
    // father name lands in PENDING_VENDOR_APPROVAL, and typing the name in is the only way to clear it.
    const REJECTED_STATUSES = [
        'VENDOR_REJECTED',
        'BD_OPS_REJECTED',
        'REJECTED',
    ];
    const VENDOR_EDITABLE_STATUSES = [
        ...REJECTED_STATUSES,
        'PENDING_VENDOR_APPROVAL',
    ];
    const showBdDobField = !!isBlueDartOrg();
    const canEditBdDob = showBdDobField && isBlueDartVendorRole();
    const isAadhaarVendorEditable = VENDOR_EDITABLE_STATUSES.includes(
        aadharState.payload?.approvalStatus ?? '',
    );
    const isPanVendorEditable = VENDOR_EDITABLE_STATUSES.includes(
        panState.payload?.approvalStatus ?? '',
    );

    // BGV Status
    const bgvStatusList = kycInfo?.bgvStatus ?? [];
    const showBGVStatus = bgvStatusList.length > 0;

    // Amazon BGV actions — org + document/BGV-state gating only (no role/rider-status
    // requirement on Initiate, unlike the BlueDart flow in RiderViewModal)
    const BGV_NO_INITIATE = ['INITIATED', 'VERIFIED', 'FAILED', 'Insufficient'];
    const insufficientBGV = bgvStatusList.some(
        (item) =>
            (item?.caseSeverity || '').trim().toLowerCase() ===
                'insufficient' ||
            (item?.status || '').trim().toLowerCase() === 'insufficient',
    );
    const panApprovedForBGV = kycInfo?.panCard?.approvalStatus === 'APPROVED';
    const aadhaarApprovedForBGV =
        kycInfo?.aadhar?.approvalStatus === 'APPROVED';
    const dlApprovedOrNotRequired =
        !(kycInfo?.dlRequiredForBGV === true) ||
        (!!kycInfo?.drivingLicense?.id &&
            kycInfo?.drivingLicense?.approvalStatus === 'APPROVED');
    const riderStatus = staffInfo?.status ?? '';

    const showInitiateBGV =
        isAmazonOrg &&
        panApprovedForBGV &&
        aadhaarApprovedForBGV &&
        dlApprovedOrNotRequired &&
        !BGV_NO_INITIATE.includes(kycInfo?.userBgvStatus ?? '') &&
        !insufficientBGV;

    // Unlike Bluedart, an Amazon rider's status is not driven by BGV: APPROVED is the normal onboarded
    // state, so only a rejected or deactivated rider is excluded.
    const showRetriggerBGV =
        isAmazonOrg &&
        insufficientBGV &&
        riderStatus !== 'REJECTED' &&
        riderStatus !== 'DEACTIVATED';
    // Profile image
    const profileImage =
        kycInfo?.selfie?.image || kycInfo?.s3ProfileLink || null;

    // Handlers
    const handleApprovalModalOpen = (
        documentId: number,
        status: 'APPROVED' | 'REJECTED',
    ) => {
        setApprovalModal({ open: true, documentId, approvalStatus: status });
    };

    const handleApprovalModalClose = () => {
        setApprovalModal({
            open: false,
            documentId: 0,
            approvalStatus: 'APPROVED',
        });
        dispatchGetSingleStaff(
            staffInfo.id,
            () => console.log('Get single staff call success'),
            () => console.log('Get single staff call failed'),
        );
    };

    const handleApprovalSuccess = () => {
        dispatchGetKycDetails(staffInfo.id);
    };

    // Save handlers for each document type
    const handleSaveAadhaar = async () => {
        if (!aadharState.payload?.documentNo?.trim()) {
            showMessage('Invalid Aadhaar Number', 'error');
            return;
        }
        if (!aadharState.payload?.imageFront) {
            showMessage('Upload Aadhaar Front Image', 'error');
            return;
        }
        if (!aadharState.payload?.imageBack) {
            showMessage('Upload Aadhaar Back Image', 'error');
            return;
        }

        setLoading(true);
        dispatchPostKycDetails(
            {
                staffId: staffInfo.id,
                data: {
                    aadhar: {
                        ...aadharState.payload,
                        docDate: aadharState.payload?.docDateMilliSeconds
                            ? Number(aadharState.payload.docDateMilliSeconds)
                            : null,
                    },
                },
            },
            () => {
                aadharState.setIsEdited(false);
                dispatchGetKycDetails(staffInfo.id);
                setLoading(false);
            },
            () => setLoading(false),
        );
    };

    const handleSaveFatherName = () => {
        const fatherName = panState.payload?.fatherName?.trim();
        if (!fatherName) {
            showMessage("Enter Father's Name", 'error');
            return;
        }
        setLoading(true);
        dispatch(
            updateFatherName(
                { userId: staffInfo.id, fatherName },
                () => {
                    showMessage('Father name saved', 'success');
                    dispatchGetKycDetails(staffInfo.id);
                    setLoading(false);
                },
                () => {
                    showMessage(
                        'Failed to save father name. Please try again.',
                        'error',
                    );
                    setLoading(false);
                },
            ) as any,
        );
    };

    const handleSavePan = async () => {
        if (!panState.payload?.documentNo?.trim()) {
            showMessage('Invalid PAN Number', 'error');
            return;
        }
        if (!panState.payload?.image) {
            showMessage('Upload PAN Image', 'error');
            return;
        }

        setLoading(true);
        dispatchPostKycDetails(
            {
                staffId: staffInfo.id,
                data: {
                    panCard: {
                        ...panState.payload,
                        docDate: panState.payload?.docDateMilliSeconds
                            ? Number(panState.payload.docDateMilliSeconds)
                            : null,
                    },
                },
            },
            () => {
                panState.setIsEdited(false);
                dispatchGetKycDetails(staffInfo.id);
                setLoading(false);
            },
            () => setLoading(false),
        );
    };

    const handleSaveRc = async () => {
        if (!rcState.payload?.documentNo?.trim()) {
            showMessage('Invalid RC Number', 'error');
            return;
        }
        if (
            !(
                rcState.payload?.image ||
                rcState.payload?.imageFront ||
                rcState.payload?.imageBack
            )
        ) {
            showMessage('Upload RC Image', 'error');
            return;
        }

        setLoading(true);
        dispatchPostKycDetails(
            { staffId: staffInfo.id, data: { rc: rcState.payload } },
            () => {
                rcState.setIsEdited(false);
                dispatchGetKycDetails(staffInfo.id);
                setLoading(false);
            },
            () => setLoading(false),
        );
    };

    const handleSavePccAppliedCopy = async () => {
        if (!pccAppliedCopyState.payload?.image) {
            showMessage('Upload PCC Applied Copy', 'error');
            return;
        }
        setLoading(true);
        dispatchPostKycDetails(
            {
                staffId: staffInfo.id,
                data: {
                    pccAppliedCopy: {
                        ...pccAppliedCopyState.payload,
                        autoKycBroker: 'MANUAL',
                    },
                },
            },
            () => {
                pccAppliedCopyState.setIsEdited(false);
                dispatchGetKycDetails(staffInfo.id);
                setLoading(false);
            },
            () => setLoading(false),
        );
    };

    const handleSavePccFinalClearance = async () => {
        if (!pccFinalClearanceState.payload?.image) {
            showMessage('Upload Final PCC Clearance Copy', 'error');
            return;
        }
        setLoading(true);
        dispatchPostKycDetails(
            {
                staffId: staffInfo.id,
                data: {
                    pccFinalClearance: {
                        ...pccFinalClearanceState.payload,
                        autoKycBroker: 'MANUAL',
                    },
                },
            },
            () => {
                pccFinalClearanceState.setIsEdited(false);
                dispatchGetKycDetails(staffInfo.id);
                setLoading(false);
            },
            () => setLoading(false),
        );
    };

    const handleSaveDl = async () => {
        const result = validateDL(dlPayload, {
            ...kycInfo?.drivingLicense,
            docDateMilliSeconds: kycInfo?.drivingLicense?.docDate
                ? moment(kycInfo.drivingLicense.docDate).valueOf()
                : null,
        });

        if (!result?.isValid) {
            showMessage(result?.errorMessage || 'Invalid DL data', 'error');
            return;
        }

        setLoading(true);
        dispatchPostKycDetails(
            {
                staffId: staffInfo.id,
                data: {
                    drivingLicense: {
                        ...dlPayload,
                        docDate: dlPayload?.docDateMilliSeconds
                            ? Number(dlPayload.docDateMilliSeconds)
                            : null,
                    },
                },
            },
            () => {
                setLoading(false);
                fetchKycInfo();
                showMessage('Driving License Updated Successfully', 'success');
            },
            () => {
                setLoading(false);
                fetchKycInfo();
                showMessage('Driving License Update Failed', 'error');
            },
        );
    };

    // DL initiation handler
    const handleInitiateDl = async () => {
        setLoading(true);
        dispatch(
            initiateDlVerification(
                staffInfo?.id,
                () => {
                    setLoading(false);
                    fetchKycInfo();
                    showMessage('Initiated Successfully', 'success');
                },
                (err: any) => {
                    setLoading(false);
                    fetchKycInfo();
                    let errorMessage = err?.message || 'Initiation Failed';
                    if (err?.status?.code === 400 && err?.status?.message) {
                        errorMessage = err.status.message;
                    }
                    showMessage(errorMessage, 'error');
                },
            ),
        );
    };

    // PAN initiation handler
    const handleInitiatePan = async () => {
        setLoading(true);
        dispatch(
            initiatePanVerification(
                staffInfo?.id,
                () => {
                    setLoading(false);
                    fetchKycInfo();
                    showMessage('Initiated Successfully', 'success');
                },
                (err: any) => {
                    setLoading(false);
                    fetchKycInfo();
                    let errorMessage = err?.message || 'Initiation Failed';
                    if (err?.status?.code === 400 && err?.status?.message) {
                        errorMessage = err.status.message;
                    }
                    showMessage(errorMessage, 'error');
                },
            ),
        );
    };

    // Amazon BGV handlers — same endpoints RiderViewModal uses
    const handleInitiateBGV = async () => {
        setLoading(true);
        dispatch(
            initiatePanVerification(
                staffInfo?.id,
                () => {
                    setLoading(false);
                    showMessage('BGV initiated successfully', 'success');
                    fetchKycInfo();
                },
                (err: any) => {
                    setLoading(false);
                    let errorMessage = err?.message || 'BGV initiation failed';
                    if (err?.status?.code === 400 && err?.status?.message) {
                        errorMessage = err.status.message;
                    }
                    showMessage(errorMessage, 'error');
                },
            ) as any,
        );
    };

    const handleRetriggerBGV = async (address: string) => {
        setLoading(true);
        dispatch(
            triggerBGVManual(
                { userId: staffInfo?.id, address },
                () => {
                    setLoading(false);
                    setRetriggerBGVOpen(false);
                    showMessage('BGV initiated successfully', 'success');
                    fetchKycInfo();
                },
                (err: any) => {
                    setLoading(false);
                    let errorMessage = err?.message || 'BGV initiation failed';
                    if (err?.status?.code === 400 && err?.status?.message) {
                        errorMessage = err.status.message;
                    }
                    showMessage(errorMessage, 'error');
                },
            ) as any,
        );
    };

    // DL specific field update
    const updateDlField = (field: string, value: any) => {
        setDlPayload((prev) =>
            prev ? { ...prev, [field]: value } : { [field]: value },
        );
        setIsDlEdited(true);
    };

    // DL Validation display
    const dlValidation = validateDL(dlPayload, {});

    // ---- Middle East save handlers ----
    const makeMESaveHandler =
        (
            docKey: string,
            state: ReturnType<typeof useDocumentState>,
            label: string,
            requireBack: boolean,
        ) =>
        async () => {
            if (!state.payload?.imageFront) {
                showMessage(`Upload ${label} front image`, 'error');
                return;
            }
            if (requireBack && !state.payload?.imageBack) {
                showMessage(`Upload ${label} back image`, 'error');
                return;
            }
            setLoading(true);
            dispatchPostKycDetails(
                { staffId: staffInfo.id, data: { [docKey]: state.payload } },
                () => {
                    state.setIsEdited(false);
                    dispatchGetKycDetails(staffInfo.id);
                    setLoading(false);
                },
                () => setLoading(false),
            );
        };

    const docPages = kycInfo?.kycDocPagesConfig ?? {};
    const docFiles = (key: string, fallback: number) =>
        docPages[key]?.files ?? fallback;
    const docNeedsDate = (key: string) => !!docPages[key]?.isDateInputNeeded;
    const docNeedsText = (key: string) =>
        docPages[key]?.isTextInputNeeded !== false;
    const handleSaveEmiratesId = makeMESaveHandler(
        'emiratesId',
        emiratesIdState,
        'Emirates ID',
        docFiles('EMIRATES_ID', 2) > 1,
    );
    const handleSaveEmiratesDl = makeMESaveHandler(
        'emiratesDl',
        emiratesDlState,
        'Emirates DL',
        docFiles('EMIRATES_DL', 2) > 1,
    );
    const handleSaveInsuranceCard = makeMESaveHandler(
        'insuranceCard',
        insuranceCardState,
        'Insurance Card',
        docFiles('INSURANCE_CARD', 1) > 1,
    );
    const handleSaveVisaCopy = makeMESaveHandler(
        'visaCopy',
        visaCopyState,
        'Visa Copy',
        docFiles('VISA_COPY', 1) > 1,
    );
    const handleSavePassport = makeMESaveHandler(
        'passport',
        passportState,
        'Passport',
        docFiles('PASSPORT', 2) > 1,
    );
    const handleSaveIqama = makeMESaveHandler(
        'iqamaId',
        iqamaState,
        'Iqama',
        docFiles('IQAMA_ID', 1) > 1,
    );

    return (
        <KycRevealProvider
            entityType="USER"
            entityId={staffInfo?.id}
            onRevealed={() => dispatchGetKycDetails(staffInfo.id)}
        >
            <Spinner loading={loading}>
                {/* Source Details Section */}
                <SourceDetailsSection
                    sourceDetails={staffInfo.sourceDetails}
                    classes={classes}
                />

                {/* Home Selfie Section */}
                {kycInfo?.homeSelfie?.id && (
                    <HomeSelfieCard
                        homeSelfie={kycInfo.homeSelfie}
                        classes={classes}
                        staffId={staffInfo.id}
                        onAddressVerified={fetchKycInfo}
                    />
                )}

                {/* Selfie — always shown */}
                <Paper style={{ marginBottom: 20 }} elevation={0}>
                    <SelfieSection
                        profileImage={profileImage}
                        classes={classes}
                    />
                </Paper>

                {/* Aadhaar Section */}
                {hasDoc('AADHAR') &&
                    (kycInfo?.aadharKycRequired ||
                        kycInfo?.uidaiKycRequired) && (
                        <>
                            {true ? (
                                <Paper
                                    style={{ marginBottom: 20 }}
                                    elevation={0}
                                >
                                    {/* Aadhaar Header with UIDAI Status */}
                                    <Box
                                        className={classes.sectionHeader}
                                        style={{ marginBottom: 16 }}
                                    >
                                        <Typography
                                            className={classes.sectionTitle}
                                            variant="h6"
                                            component="span"
                                            gutterBottom
                                        >
                                            Aadhaar Details
                                        </Typography>

                                        {/* UIDAI Status Badge */}
                                        {kycInfo?.uidaiKycRequired && (
                                            <UidaiStatusBadge
                                                uidaiKycVerified={
                                                    !!kycInfo?.uidaiKycVerified
                                                }
                                                classes={classes}
                                            />
                                        )}

                                        <div style={{ flexGrow: 1 }}>
                                            <Divider
                                                className={classes.dividerLine}
                                            />
                                        </div>
                                    </Box>
                                    {/* Aadhaar Document Card */}
                                    {kycInfo?.aadharKycRequired && (
                                        <EditableKYCDocumentCard
                                            title="Aadhaar"
                                            documentType="AADHAAR"
                                            document={
                                                aadharState.payload || null
                                            }
                                            isEdited={aadharState.isEdited}
                                            isAutoVerified={
                                                isAadhaarAutoVerified
                                            }
                                            isKycRequired={
                                                kycInfo?.aadharKycRequired
                                            }
                                            showBackImage={true}
                                            showDOBField={showBdDobField}
                                            dobEnableEdit={
                                                canEditBdDob &&
                                                isAadhaarVendorEditable
                                            }
                                            enableEdit={
                                                !isAadhaarVerifiedByDigiLocker &&
                                                !isAadhaarAutoVerified
                                            }
                                            isPendingAtOps={
                                                isAadhaarPendingAtOps
                                            }
                                            uidaiInfo={kycInfo?.uidaiAadhar}
                                            uidaiKycRequired={
                                                kycInfo?.uidaiKycRequired
                                            }
                                            uidaiKycVerified={
                                                kycInfo?.uidaiKycVerified
                                            }
                                            onDocumentChange={
                                                aadharState.updateField
                                            }
                                            onSave={handleSaveAadhaar}
                                            onApprove={() =>
                                                handleApprovalModalOpen(
                                                    aadharState.payload?.id ||
                                                        0,
                                                    'APPROVED',
                                                )
                                            }
                                            onReject={() =>
                                                handleApprovalModalOpen(
                                                    aadharState.payload?.id ||
                                                        0,
                                                    'REJECTED',
                                                )
                                            }
                                            onEditStateChange={
                                                aadharState.setIsEdited
                                            }
                                        />
                                    )}
                                </Paper>
                            ) : (
                                <Paper
                                    style={{ marginBottom: 20 }}
                                    elevation={0}
                                >
                                    <KYCDocumentNotProvidedCard
                                        title="Aadhaar"
                                        documentType="AADHAAR"
                                    />
                                </Paper>
                            )}
                        </>
                    )}

                {/* PAN Card Section */}
                {hasDoc('PANCARD') && (
                    <Paper style={{ marginBottom: 20 }} elevation={0}>
                        <EditableKYCDocumentCard
                            title="Pan Card"
                            documentType="PAN"
                            document={panState.payload || null}
                            isEdited={panState.isEdited}
                            isAutoVerified={isPANAutoVerified}
                            showBackImage={false}
                            showDOBField={showBdDobField}
                            dobEnableEdit={canEditBdDob && isPanVendorEditable}
                            showFatherNameField={showBdDobField}
                            fatherNameEnableEdit={
                                canEditBdDob && isPanVendorEditable
                            }
                            enableEdit={
                                panState?.payload?.dlCaseCheck === 'INITIATED'
                                    ? false
                                    : !isPanVerifiedByDigiLocker &&
                                      !isPANAutoVerified
                            }
                            isPendingAtOps={isPanPendingAtOps}
                            onDocumentChange={panState.updateField}
                            onInitiate={
                                isBlueDartOrg() ? undefined : handleInitiatePan
                            }
                            onSave={handleSavePan}
                            onFatherNameSave={handleSaveFatherName}
                            onApprove={() =>
                                handleApprovalModalOpen(
                                    panState.payload?.id || 0,
                                    'APPROVED',
                                )
                            }
                            onReject={() =>
                                handleApprovalModalOpen(
                                    panState.payload?.id || 0,
                                    'REJECTED',
                                )
                            }
                            onEditStateChange={panState.setIsEdited}
                        />
                    </Paper>
                )}
                {/* Amazon BGV actions */}
                {(showInitiateBGV || showRetriggerBGV) && (
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        mb={2.5}
                        style={{ gap: 8 }}
                    >
                        {showInitiateBGV && (
                            <Button
                                variant="contained"
                                style={{ background: '#43419E', color: '#fff' }}
                                onClick={handleInitiateBGV}
                            >
                                Initiate BGV
                            </Button>
                        )}
                        {showRetriggerBGV && (
                            <Button
                                variant="contained"
                                style={{ background: '#43419E', color: '#fff' }}
                                onClick={() => setRetriggerBGVOpen(true)}
                            >
                                Retrigger BGV
                            </Button>
                        )}
                    </Box>
                )}

                {/* Vehicle RC Section */}
                {hasDoc('RC') && (
                    <Paper style={{ marginBottom: 20 }} elevation={0}>
                        <EditableKYCDocumentCard
                            title="Vehicle RC"
                            documentType="RC"
                            document={rcState.payload || null}
                            isEdited={rcState.isEdited}
                            isAutoVerified={isRCAutoVerified}
                            showBackImage={true}
                            enableEdit={!isRCAutoVerified}
                            isPendingAtOps={isRcPendingAtOps}
                            onDocumentChange={rcState.updateField}
                            onSave={handleSaveRc}
                            onApprove={() =>
                                handleApprovalModalOpen(
                                    rcState.payload?.id || 0,
                                    'APPROVED',
                                )
                            }
                            onReject={() =>
                                handleApprovalModalOpen(
                                    rcState.payload?.id || 0,
                                    'REJECTED',
                                )
                            }
                            onEditStateChange={rcState.setIsEdited}
                        />
                    </Paper>
                )}

                {/* PCC Applied Copy Section — BlueDart vendor only */}
                {showPccDocs && (
                    <Paper style={{ marginBottom: 20 }} elevation={0}>
                        <EditableKYCDocumentCard
                            title="PCC Applied Copy"
                            documentType="PCC_APPLIED_COPY"
                            document={pccAppliedCopyState.payload || null}
                            isEdited={pccAppliedCopyState.isEdited}
                            isAutoVerified={false}
                            showBackImage={false}
                            showTextInput={false}
                            showActions={false}
                            enableEdit={true}
                            onDocumentChange={pccAppliedCopyState.updateField}
                            onSave={handleSavePccAppliedCopy}
                            onEditStateChange={pccAppliedCopyState.setIsEdited}
                            onApprove={() => {}}
                            onReject={() => {}}
                        />
                    </Paper>
                )}

                {/* Final PCC Clearance Copy Section — BlueDart vendor only */}
                {showPccDocs && (
                    <Paper style={{ marginBottom: 20 }} elevation={0}>
                        <EditableKYCDocumentCard
                            title="Final PCC Clearance Copy"
                            documentType="PCC_FINAL_CLEARANCE"
                            document={pccFinalClearanceState.payload || null}
                            isEdited={pccFinalClearanceState.isEdited}
                            isAutoVerified={false}
                            showBackImage={false}
                            showTextInput={false}
                            showActions={false}
                            enableEdit={true}
                            onDocumentChange={
                                pccFinalClearanceState.updateField
                            }
                            onSave={handleSavePccFinalClearance}
                            onEditStateChange={
                                pccFinalClearanceState.setIsEdited
                            }
                            onApprove={() => {}}
                            onReject={() => {}}
                        />
                    </Paper>
                )}

                {/* Driving License Section */}
                {hasDoc('DRIVING_LICENSE') ? (
                    <Paper style={{ marginBottom: 20 }} elevation={0}>
                        <EditableKYCDocumentCard
                            title="DL"
                            documentType="DL"
                            document={dlPayload || null}
                            isEdited={isDlEdited}
                            isAutoVerified={isDLAutoVerified}
                            showBackImage={true}
                            showDOBField={true}
                            enableEdit={enableDLUpdate}
                            isFailed={isDLFailed}
                            isPendingAtOps={isDLPendingAtOps}
                            onDocumentChange={updateDlField}
                            onInitiate={
                                isBlueDartOrg() ? undefined : handleInitiateDl
                            }
                            onSave={handleSaveDl}
                            onApprove={() =>
                                handleApprovalModalOpen(
                                    dlPayload?.id || 0,
                                    'APPROVED',
                                )
                            }
                            onReject={() =>
                                handleApprovalModalOpen(
                                    dlPayload?.id || 0,
                                    'REJECTED',
                                )
                            }
                            onEditStateChange={setIsDlEdited}
                        />

                        {/* DL Validation Error */}
                        {enableDLUpdate &&
                            !dlValidation?.isValid &&
                            dlValidation?.errorMessage && (
                                <Typography
                                    style={{
                                        marginTop: '8px',
                                        color: '#f44336',
                                        fontWeight: 600,
                                        padding: '0 20px 20px',
                                    }}
                                >
                                    {dlValidation.errorMessage}
                                </Typography>
                            )}
                    </Paper>
                ) : null}

                {/* ── Middle East KYC Doc Sections (shown when configured in org settings) ── */}
                {[
                    {
                        key: 'EMIRATES_ID' as DocumentType,
                        label: 'Emirates ID',
                        state: emiratesIdState,
                        onSave: handleSaveEmiratesId,
                    },
                    {
                        key: 'IQAMA_ID' as DocumentType,
                        label: 'Iqama ID',
                        state: iqamaState,
                        onSave: handleSaveIqama,
                    },
                    {
                        key: 'EMIRATES_DL' as DocumentType,
                        label: 'Driving Licence',
                        state: emiratesDlState,
                        onSave: handleSaveEmiratesDl,
                    },
                    {
                        key: 'INSURANCE_CARD' as DocumentType,
                        label: 'Insurance Card',
                        state: insuranceCardState,
                        onSave: handleSaveInsuranceCard,
                    },
                    {
                        key: 'VISA_COPY' as DocumentType,
                        label: 'Visa Copy',
                        state: visaCopyState,
                        onSave: handleSaveVisaCopy,
                    },
                    {
                        key: 'PASSPORT' as DocumentType,
                        label: 'Passport',
                        state: passportState,
                        onSave: handleSavePassport,
                    },
                ]
                    .filter(({ key }) => hasDoc(key))
                    .map(({ key, label, state, onSave }) => (
                        <Paper
                            key={key}
                            style={{ marginBottom: 20 }}
                            elevation={0}
                        >
                            <EditableKYCDocumentCard
                                title={label}
                                documentType={key}
                                document={state.payload || null}
                                isEdited={state.isEdited}
                                isAutoVerified={false}
                                showBackImage={docFiles(key, 2) > 1}
                                showDOBField={docNeedsDate(key)}
                                showTextInput={docNeedsText(key)}
                                enableEdit={true}
                                onDocumentChange={state.updateField}
                                onSave={onSave}
                                onApprove={() =>
                                    handleApprovalModalOpen(
                                        state.payload?.id || 0,
                                        'APPROVED',
                                    )
                                }
                                onReject={() =>
                                    handleApprovalModalOpen(
                                        state.payload?.id || 0,
                                        'REJECTED',
                                    )
                                }
                                onEditStateChange={state.setIsEdited}
                            />
                        </Paper>
                    ))}

                {/* BGV Status Section — Amazon org gets the enhanced timeline */}
                {showBGVStatus &&
                    (isAmazonOrg ? (
                        <BGVStatusV2 bgvStatusList={bgvStatusList} />
                    ) : (
                        <BackgroundVerification bgvStatusList={bgvStatusList} />
                    ))}

                {/* Approval Modal */}
                <KYCApprovalModal
                    open={approvalModal.open}
                    onClose={handleApprovalModalClose}
                    staffId={staffInfo.id}
                    documentId={approvalModal.documentId}
                    approvalStatus={approvalModal.approvalStatus}
                    onSuccess={handleApprovalSuccess}
                />

                {/* Retrigger BGV Modal */}
                {retriggerBGVOpen && (
                    <RetriggerBGVModal
                        open={retriggerBGVOpen}
                        onClose={() => setRetriggerBGVOpen(false)}
                        name={staffInfo?.name}
                        contactNumber={staffInfo?.contactNumber}
                        dob={kycInfo?.aadhar?.autoKycDocumentDob}
                        defaultAddress={kycInfo?.uidaiAadhar?.address ?? ''}
                        loading={loading}
                        onSubmit={handleRetriggerBGV}
                        title={
                            insufficientBGV
                                ? 'Retrigger Background Verification'
                                : 'Initiate Background Verification'
                        }
                    />
                )}
            </Spinner>
        </KycRevealProvider>
    );
};

export default VerifyKycV2;
